import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dummyUser, logoColor, logoLight } from '../ui/media';
import { Sun, Moon } from 'lucide-react';
import { useStore } from '@tanstack/react-store'
import { themeStore, setDarkMode } from '../../stores/theme'
import { useLogout, useUser } from "../../hooks/useAuth"
import toast from 'react-hot-toast';

function Header() {
    const { isDarkMode } = useStore(themeStore)
    const [navOpen, setNavOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [userData, setUserData] = useState(null);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const logout = useLogout()
    const user = useUser()

    const toggleTheme = () => {
        setDarkMode(!isDarkMode);
    };

    const { isError, data } = user

    const handleLogout = () => {
        logout.mutate(null, {
            onSuccess: () => {
                navigate('/login');
                toast.success('Logged out successfully');
            },
            onError: () => {
                navigate('/login');
                toast.error('Session expired, logged out');
            }
        })
    };

    useEffect(() => {
        if (isError) {
            handleLogout()
        }
        setUserData(data?.data?.data)
    }, [data, isError])

    const toggleNav = () => {
        setNavOpen(!navOpen);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <header className={`w-full m-0 fixed ${isDarkMode ? "bg-[rgba(255,255,255,0.6)] border-b border-white-200" : "bg-white border-b border-gray-200"}  flex items-center justify-between shadow-sm z-10 px-16`}>
            <div className="absolute z-99 top-4 right-4">
                <button
                    onClick={toggleTheme}
                    className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 text-yellow-400' : 'bg-white text-gray-800 shadow'}`}>
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
            </div>
            <div className="container px-4 py-3 flex justify-between items-center">
                <div className="flex items-center">
                    <img
                        onClick={() => navigate('/lander')} 
                        className="h-12 cursor-pointer" 
                        src={`${isDarkMode ? logoLight : logoColor}`} 
                        alt="Logo" 
                    />
                </div>
                <button onClick={toggleNav} className={`md:hidden ${isDarkMode?"text-white":"text-gray-800"}`}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    </svg>
                </button>
                <nav className={`absolute md:relative top-full left-0 w-full md:w-auto bg-gray-100 md:bg-transparent p-4 md:p-0 transform ${navOpen ? 'scale-y-100' : 'scale-y-0 md:scale-y-100'} origin-top transition-transform md:flex md:items-center`}>
                    <ul className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6">
                        {[{
                            name: 'Home',
                            url: '/lander'
                        }, {
                            name: 'About Us',
                            url: '/lander#about'
                        }, {
                            name: 'Services',
                            url: '/lander#services',
                        }, {
                            name: 'Subscriptions',
                            url: '/subscriptions',
                        }, {
                            name: 'FAQ',
                            url: '/lander#faq'
                        }, {
                            name: 'Contact Us',
                            url: '/lander#contact'
                        }].map((item, index) => (
                            <li key={index}><a href={`${item.url}`} className={`text-gray-800`}>{item?.name}</a></li>
                        ))}
                        {userData &&
                            <li key={'dashboard'}>
                                <a href={`${userData?.user_type == 2? '/events/list': '/locations/list'}`} className={`text-gray-800`}>Dashboard</a>
                            </li>
                        }
                    </ul>
                    {userData ?
                        <div className="mt-2 md:mt-0 md:px-5 py-1 relative" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className={`flex items-center font-medium text-gray-800 focus:outline-none`}>
                                <img
                                    className="h-8 rounded-full mr-2"
                                    src={userData?.profile_pic ? `${process.env.REACT_APP_IMAGE_URL}/${userData?.profile_pic}` : dummyUser}
                                    alt="Profile"
                                />
                                <span className="">{userData?.name}</span>
                                <svg
                                    className="ml-2 h-5 w-5 text-gray-800"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true">
                                    <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                            {dropdownOpen && (
                                <div className={`absolute md:right-0 mt-2 w-auto rounded-md shadow-lg py-1 ${isDarkMode ? "bg-gray-800" : "bg-white"} focus:outline-none`}>
                                    <div className="px-4 py-3">
                                        <p className={`text-sm ${isDarkMode ? "text-white" : "text-gray-800"} `}>{userData?.name}</p>
                                        <p className={`text-sm ${isDarkMode ? "text-white" : "text-gray-800"} font-medium text-gray-500 truncate`}>
                                            {userData?.email}
                                        </p>
                                    </div>
                                    <hr />
                                    <a
                                        href="/account"
                                        className={`block px-4 py-2 text-sm ${isDarkMode ? "text-white hover:text-white" : "text-black hover:text-black"} `}>
                                        Account
                                    </a>
                                    <button
                                        onClick={handleLogout}
                                        className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? "text-white hover:text-white" : "text-black hover:text-black"} `}>
                                        Log Out
                                    </button>
                                </div>
                            )}
                        </div> :
                        <button
                            onClick={() => navigate('/login')}
                            className="mt-4 md:mt-0 md:ml-6 bg-blue-600 text-white px-5 py-1 rounded-full font-medium">
                            Log In
                        </button>
                    }
                </nav>
            </div>
        </header>
    );
}
export default Header;